import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { OAuthService } from '../../services/oauthService';
import { getServiceClass } from '../../services/serviceCache';
import { logger } from '../../services/loggingService';
import { SubscriptionService } from '../../services/subscriptionService';
import { ConfirmModal } from '../Common/ConfirmModal';
import { dbOperation } from '../../utils/database';
import toast from 'react-hot-toast';
import { Github, Loader2, Layers, Plus, FolderPlus, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Heading, Text, Caption } from '../ui/Typography';
import { Badge } from '../ui/Badge';
import { LoadingState } from '../Common/LoadingState';
import { EmptyState } from '../Common/ErrorState';
import { cn } from '../../utils/cn';
import { spacing } from '../../utils/spacing';

interface CreateProjectModalProps {
  onClose: () => void;
  onSuccess: (projectId?: string) => void;
}

interface GitHubRepo {
  id: number;
  name: string;
  full_name: string;
  private: boolean;
  description: string | null;
  html_url: string;
}

interface Organization {
  id: string;
  name: string;
  role: string;
}

export function CreateProjectModal({
  onClose,
  onSuccess,
}: CreateProjectModalProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [githubConnected, setGithubConnected] = useState(false);
  const [repositories, setRepositories] = useState<GitHubRepo[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'import' | 'create'>('import');
  const [newRepoName, setNewRepoName] = useState('');
  const [newRepoDescription, setNewRepoDescription] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [userOrganizations, setUserOrganizations] = useState<Organization[]>(
    []
  );
  const [selectedOrganization, setSelectedOrganization] =
    useState<string>('personal');
  const [upgradeConfirm, setUpgradeConfirm] = useState<{
    isOpen: boolean;
    message: string;
    upgradeUrl: string;
  }>({
    isOpen: false,
    message: '',
    upgradeUrl: '',
  });

  useEffect(() => {
    checkGitHubConnection();
    loadUserOrganizations();
  }, [user]);

  useEffect(() => {
    if (githubConnected && repositories.length === 0) {
      loadGitHubRepositories();
    }
  }, [githubConnected, repositories.length]);

  const loadUserOrganizations = useCallback(async () => {
    if (!user) return;

    const result = await dbOperation(
      supabase.rpc('get_user_organizations', {
        user_id: user.id,
      }),
      {
        errorMessage: 'Failed to load organizations',
        showErrorToast: false
      }
    );

    if (result) {
      setUserOrganizations(result || []);
    }
  }, [user]);

  const checkGitHubConnection = useCallback(async () => {
    if (!user) return;

    const data = await dbOperation(
      supabase
        .from('user_connections')
        .select('*')
        .eq('user_id', user.id)
        .eq('provider', 'github')
        .eq('status', 'active')
        .maybeSingle(),
      {
        errorMessage: 'Failed to check GitHub connection',
        showErrorToast: false
      }
    );

    setGithubConnected(!!data);
  }, [user]);

  const loadGitHubRepositories = useCallback(async () => {
    if (!user) return;

    try {
      setLoadingRepos(true);
      const repos = await OAuthService.getGitHubRepositories(user.id);
      setRepositories(repos);
    } catch (error) {
      logger.error('Error loading repositories:', {
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error('Failed to load repositories. Please reconnect GitHub.');
    } finally {
      setLoadingRepos(false);
    }
  }, [user]);

  const handleConnectGitHub = () => {
    OAuthService.initiateOAuthFlow('github');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    if (!selectedRepo) {
      toast.error('Please select a repository');
      return;
    }

    if (!githubConnected) {
      toast.error('GitHub connection required');
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage('Checking subscription limits...');

      // Enforce subscription limits using comprehensive service
      const { SubscriptionLimitEnforcementService } = await import(
        '../../services/subscriptionLimitEnforcementService'
      );
      const limitCheck =
        await SubscriptionLimitEnforcementService.checkProjectLimit(user.id);

      if (!limitCheck.allowed) {
        toast.error(limitCheck.message || 'Project limit reached');

        if (limitCheck.requiresUpgrade) {
          setTimeout(() => {
            setUpgradeConfirm({
              isOpen: true,
              message: limitCheck.message || 'Project limit reached',
              upgradeUrl: limitCheck.upgradeUrl || '/settings/billing',
            });
          }, 500);
        }
        setLoading(false);
        return;
      }

      const selectedRepoData = repositories.find(
        r => r.full_name === selectedRepo
      );
      if (!selectedRepoData) {
        toast.error('Selected repository not found');
        setLoading(false);
        return;
      }

      setLoadingMessage('Connecting to GitHub...');

      // eslint-disable-next-line no-console
      console.log('[DEBUG] Starting GitHub import process...');

      const GitHubProjectImportService = await getServiceClass(
        'githubProjectImportService',
        'GitHubProjectImportService'
      );

      // eslint-disable-next-line no-console
      console.log('[DEBUG] Service class loaded:', GitHubProjectImportService);

      setLoadingMessage('Importing repository (this may take a few minutes)...');

      const result = await GitHubProjectImportService.importExistingProject({
        userId: user.id,
        repoUrl: selectedRepoData.html_url,
        projectName: selectedRepoData.name,
        description: selectedRepoData.description || undefined,
        analyzeExistingCode: true,
      });

      // eslint-disable-next-line no-console
      console.log('[DEBUG] Import result:', result);

      if (!result.success) {
        toast.error(result.error || 'Failed to import project');
        setLoading(false);
        return;
      }

      // Check if analysis failed but project was created
      if ((result as any).analysisError) {
        logger.warn('Project imported but analysis failed', {
          projectId: result.projectId,
          analysisError: (result as any).analysisError,
        });
        toast.error(
          `Project imported but analysis failed: ${(result as any).analysisError}. You can retry analysis later.`,
          { duration: 6000 }
        );
      } else {
        toast.success(
          'Project imported and analysis started! This may take a few minutes...',
          { duration: 5000 }
        );
      }

      // Import succeeded - close modal and continue with setup
      onSuccess(result.projectId);
      onClose();

      // Continue with setup in background (non-blocking)
      try {
        await SubscriptionService.incrementUsage(user.id, 'projects');

        const setupMessage = (result as any).analysisError
          ? 'Analysis failed initially — continuing with setup and agent analysis...'
          : 'Setting up automation...';
        toast.success(setupMessage, { duration: 3000 });

        const ProjectSetupService = await getServiceClass(
          'projectSetupService',
          'ProjectSetupService'
        );
        await ProjectSetupService.setupImportedProject({
          projectId: result.projectId,
          userId: user.id,
          setupBlueprint: true,
          setupJira: true,
          assignDefaultAgents: true,
        });

        toast.success('Project setup complete! Ready to use.');
      } catch (setupError) {
        logger.error('Project setup error:', {
          error:
            setupError instanceof Error
              ? setupError.message
              : String(setupError),
        });
        toast.error(
          'Project imported but automation setup failed. You can set up manually.'
        );
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('Error creating project:', {
        error: errorMessage,
        userId: user.id,
        repoUrl: selectedRepo,
      });

      // Provide specific error messages based on error type
      if (errorMessage.includes('API key') || errorMessage.includes('not configured')) {
        toast.error(
          'AI analysis is not available. The platform requires AI configuration to analyze your codebase. Please contact support.',
          { duration: 10000 }
        );
      } else if (errorMessage.includes('404') || errorMessage.includes('Not Found')) {
        toast.error(
          'Repository not found or access denied. Please verify the repository exists and you have access.',
          { duration: 8000 }
        );
      } else if (errorMessage.includes('rate limit')) {
        toast.error(
          'GitHub rate limit exceeded. Please wait a few minutes and try again.',
          { duration: 8000 }
        );
      } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
        toast.error(
          'Network error. Please check your connection and try again.',
          { duration: 6000 }
        );
      } else {
        toast.error(
          `Failed to import project: ${errorMessage}`,
          { duration: 8000 }
        );
      }
      setLoading(false);
    }
  };

  const handleCreateNewRepository = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    if (!newRepoName.trim()) {
      toast.error('Repository name is required');
      return;
    }

    if (!githubConnected) {
      toast.error('GitHub connection required');
      return;
    }

    try {
      setLoading(true);

      // Enforce subscription limits
      const { SubscriptionLimitEnforcementService } = await import(
        '../../services/subscriptionLimitEnforcementService'
      );
      const limitCheck =
        await SubscriptionLimitEnforcementService.checkProjectLimit(user.id);

      if (!limitCheck.allowed) {
        toast.error(limitCheck.message || 'Project limit reached');

        if (limitCheck.requiresUpgrade) {
          setTimeout(() => {
            setUpgradeConfirm({
              isOpen: true,
              message: limitCheck.message || 'Project limit reached',
              upgradeUrl: limitCheck.upgradeUrl || '/settings/billing',
            });
          }, 500);
        }
        return;
      }

      // Enterprise-grade atomic operation: Create project, then GitHub repo, with rollback on failure
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          user_id: user.id,
          name: newRepoName,
          description: newRepoDescription,
          default_branch: 'main',
          organization_id:
            selectedOrganization === 'personal' ? null : selectedOrganization,
        })
        .select('id')
        .single();

      if (projectError || !project) {
        logger.error('Failed to create project in database', {
          error: projectError,
          projectName: newRepoName,
          userId: user.id,
        });
        toast.error(projectError?.message || 'Failed to create project');
        setLoading(false);
        return;
      }

      // Create GitHub repository - if this fails, rollback project creation
      const GitHubScaffoldService = await getServiceClass(
        'githubScaffoldService',
        'GitHubScaffoldService'
      );
      const scaffoldResult = await GitHubScaffoldService.scaffoldRepository({
        userId: user.id,
        projectId: project.id,
        repoName: newRepoName,
        repoDescription: newRepoDescription,
        isPrivate: isPrivate,
        manifestData: { files: [] }, // Will be created later
      });

      if (!scaffoldResult.success) {
        // Rollback: Delete project if GitHub creation fails (atomic operation)
        logger.warn('GitHub repository creation failed, rolling back project', {
          projectId: project.id,
          error: scaffoldResult.error,
        });
        await supabase.from('projects').delete().eq('id', project.id);

        toast.error(
          scaffoldResult.error ||
            'Failed to create GitHub repository. Project creation was rolled back.'
        );
        setLoading(false);
        return;
      }

      await SubscriptionService.incrementUsage(user.id, 'projects');

      // Set up project automation
      toast.success('Repository created! Setting up automation...');

      try {
        const ProjectSetupService = await getServiceClass(
          'projectSetupService',
          'ProjectSetupService'
        );
        await ProjectSetupService.setupImportedProject({
          projectId: project.id,
          userId: user.id,
          setupBlueprint: true,
          setupJira: true,
          assignDefaultAgents: true,
        });

        toast.success('Project setup complete! Ready to use.');
      } catch (setupError) {
        logger.error('Project setup error:', {
          error:
            setupError instanceof Error
              ? setupError.message
              : String(setupError),
        });
        toast.error(
          'Repository created but automation setup failed. You can set up manually.'
        );
      }

      onSuccess(project.id);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.error('Error creating repository:', {
        error: errorMessage,
        userId: user.id,
        repoName: newRepoName,
      });

      // Provide specific error messages
      if (errorMessage.includes('already exists')) {
        toast.error(
          `Repository "${newRepoName}" already exists in your GitHub account. Please choose a different name.`,
          { duration: 8000 }
        );
      } else if (errorMessage.includes('permission') || errorMessage.includes('access')) {
        toast.error(
          'Permission denied. Please verify your GitHub token has repository creation access.',
          { duration: 8000 }
        );
      } else if (errorMessage.includes('rate limit')) {
        toast.error(
          'GitHub rate limit exceeded. Please wait a few minutes and try again.',
          { duration: 8000 }
        );
      } else {
        toast.error(
          `Failed to create repository: ${errorMessage}`,
          { duration: 8000 }
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col p-0">
        <div 
          className="p-6 flex-shrink-0"
          style={{
            background: 'linear-gradient(to right, rgb(var(--color-primary)), rgb(var(--color-primary-hover)))',
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 backdrop-blur-sm"
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: 'var(--radius-xl)',
                }}
              >
                <Layers className="w-6 h-6 text-white" />
              </div>
              <div>
                <Heading level={2} className="text-white mb-1">
                  Connect Your Repository
                </Heading>
                <Text size="sm" className="text-white/90">
                  Bridge your GitHub repository with AI-powered SDLC management
                </Text>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors p-1 disabled:opacity-50"
              disabled={loading}
              style={{ transition: 'var(--transition-base)' }}
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-border">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('import')}
              className={cn(
                'flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'import'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-tertiary hover:text-secondary'
              )}
              style={{
                borderBottomColor: activeTab === 'import' 
                  ? 'rgb(var(--color-primary))' 
                  : 'transparent',
                color: activeTab === 'import'
                  ? 'rgb(var(--color-primary))'
                  : 'rgb(var(--color-text-tertiary))',
                transition: 'var(--transition-base)',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'import') {
                  e.currentTarget.style.color = 'rgb(var(--color-text-secondary))';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'import') {
                  e.currentTarget.style.color = 'rgb(var(--color-text-tertiary))';
                }
              }}
            >
              <div className="flex items-center justify-center gap-2">
                <Github className="w-4 h-4" />
                Import Existing Repository
              </div>
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={cn(
                'flex-1 py-3 px-4 text-sm font-medium border-b-2 transition-colors',
                activeTab === 'create'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-tertiary hover:text-secondary'
              )}
              style={{
                borderBottomColor: activeTab === 'create' 
                  ? 'rgb(var(--color-primary))' 
                  : 'transparent',
                color: activeTab === 'create'
                  ? 'rgb(var(--color-primary))'
                  : 'rgb(var(--color-text-tertiary))',
                transition: 'var(--transition-base)',
              }}
              onMouseEnter={(e) => {
                if (activeTab !== 'create') {
                  e.currentTarget.style.color = 'rgb(var(--color-text-secondary))';
                }
              }}
              onMouseLeave={(e) => {
                if (activeTab !== 'create') {
                  e.currentTarget.style.color = 'rgb(var(--color-text-tertiary))';
                }
              }}
            >
              <div className="flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" />
                Create New Repository
              </div>
            </button>
          </nav>
        </div>

        <div className={cn('flex-1 overflow-y-auto', spacing.section.padding, spacing.section.gap)}>
          {activeTab === 'import' && (
            <>
              {!githubConnected ? (
                <EmptyState
                  title="GitHub Connection Required"
                  message="Connect your GitHub account to import and analyze repositories. This is required to use the platform."
                  icon={Github}
                  actionLabel="Connect GitHub Account"
                  onAction={handleConnectGitHub}
                />
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className='space-y-5 flex flex-col h-full'
                >
                  <div className='flex-1 overflow-y-auto space-y-5 pr-2'>
                    {/* Organization Selection */}
                    {userOrganizations.length > 0 && (
                      <div>
                        <label className='block text-sm font-semibold text-gray-900 dark:text-white mb-3'>
                          Project Owner
                        </label>
                        <select
                          value={selectedOrganization}
                          onChange={e =>
                            setSelectedOrganization(e.target.value)
                          }
                          className='w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all'
                        >
                          <option value='personal'>
                            Personal Project (Only You)
                          </option>
                          {userOrganizations.map(org => (
                            <option key={org.id} value={org.id}>
                              {org.name} (Team - All Members)
                            </option>
                          ))}
                        </select>
                        <p className='text-xs text-gray-500 dark:text-gray-400 mt-2'>
                          {selectedOrganization === 'personal'
                            ? 'This project will only be visible to you'
                            : 'All organization members will be able to access and collaborate on this project'}
                        </p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold mb-3 text-text">
                        Select Repository{' '}
                        <span className="text-destructive">*</span>
                      </label>

                      {loadingRepos ? (
                        <LoadingState 
                          message="Loading your repositories..."
                          size="md"
                        />
                      ) : repositories.length === 0 ? (
                        <EmptyState
                          title="No repositories found"
                          message="Make sure you have repositories in your GitHub account"
                          icon={Github}
                        />
                      ) : (
                        <>
                          <div className='grid gap-3 max-h-64 overflow-y-auto pr-2'>
                            {repositories.map(repo => (
                              <label
                                key={repo.id}
                                className={cn(
                                  'relative flex items-start gap-4 p-4 border-2 rounded-xl cursor-pointer transition-all',
                                  selectedRepo === repo.full_name && 'ring-2'
                                )}
                                style={{
                                  borderColor: selectedRepo === repo.full_name
                                    ? 'rgb(var(--color-primary))'
                                    : 'rgb(var(--color-border))',
                                  backgroundColor: selectedRepo === repo.full_name
                                    ? 'rgb(var(--color-primary-light))'
                                    : 'transparent',
                                  borderRadius: 'var(--radius-xl)',
                                  transition: 'var(--transition-smooth)',
                                }}
                                onMouseEnter={(e) => {
                                  if (selectedRepo !== repo.full_name) {
                                    e.currentTarget.style.borderColor = 'rgb(var(--color-primary))';
                                    e.currentTarget.style.backgroundColor = 'rgb(var(--color-background-secondary))';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (selectedRepo !== repo.full_name) {
                                    e.currentTarget.style.borderColor = 'rgb(var(--color-border))';
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                  }
                                }}
                              >
                                <input
                                  type="radio"
                                  name="repository"
                                  value={repo.full_name}
                                  checked={selectedRepo === repo.full_name}
                                  onChange={e =>
                                    setSelectedRepo(e.target.value)
                                  }
                                  className="mt-1 w-4 h-4"
                                  style={{
                                    accentColor: 'rgb(var(--color-primary))',
                                  }}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Text size="sm" weight="semibold" className="truncate">
                                      {repo.full_name}
                                    </Text>
                                    <Badge variant={repo.private ? 'warning' : 'default'}>
                                      {repo.private ? 'Private' : 'Public'}
                                    </Badge>
                                  </div>
                                  {repo.description && (
                                    <Text size="xs" className="line-clamp-2 text-text-secondary">
                                      {repo.description}
                                    </Text>
                                  )}
                                </div>
                              </label>
                            ))}
                          </div>
                          <Caption className="mt-3">
                            {repositories.length}{' '}
                            {repositories.length === 1
                              ? 'repository'
                              : 'repositories'}{' '}
                            available
                          </Caption>
                        </>
                      )}
                    </div>
                  </div>

                  <div 
                    className="flex gap-3 pt-4 flex-shrink-0"
                    style={{
                      borderTop: '1px solid rgb(var(--color-border))',
                    }}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      className="flex-1"
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="default"
                      className="flex-1"
                      disabled={loading || !selectedRepo}
                      title={
                        !selectedRepo
                          ? 'Please select a repository first'
                          : 'Import and analyze the selected repository'
                      }
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          {loadingMessage || 'Importing & Analyzing...'}
                        </span>
                      ) : (
                        'Import & Analyze Repository'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}

          {activeTab === 'create' && (
            <>
              {!githubConnected ? (
                <EmptyState
                  title="GitHub Connection Required"
                  message="Connect your GitHub account to create new repositories. This is required to use the platform."
                  icon={FolderPlus}
                  actionLabel="Connect GitHub Account"
                  onAction={handleConnectGitHub}
                />
              ) : (
                <form
                  onSubmit={handleCreateNewRepository}
                  className={cn('space-y-5 flex flex-col h-full', spacing.form.group)}
                >
                  <div className={cn('flex-1 overflow-y-auto pr-2', spacing.form.group)}>
                    {/* Organization Selection */}
                    {userOrganizations.length > 0 && (
                      <div>
                        <label className="block text-sm font-semibold mb-3 text-text">
                          Project Owner
                        </label>
                        <select
                          value={selectedOrganization}
                          onChange={e =>
                            setSelectedOrganization(e.target.value)
                          }
                          className="w-full rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-offset-2"
                          style={{
                            padding: spacing.form.input,
                            border: '2px solid rgb(var(--color-input))',
                            backgroundColor: 'rgb(var(--color-background))',
                            color: 'rgb(var(--color-text))',
                            borderRadius: 'var(--radius-xl)',
                            transition: 'var(--transition-base)',
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = 'rgb(var(--color-border-focus))';
                            e.currentTarget.style.boxShadow = '0 0 0 2px rgba(var(--color-primary), 0.2)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = 'rgb(var(--color-input))';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <option value='personal'>
                            Personal Project (Only You)
                          </option>
                          {userOrganizations.map(org => (
                            <option key={org.id} value={org.id}>
                              {org.name} (Team - All Members)
                            </option>
                          ))}
                        </select>
                        <Caption className="mt-2">
                          {selectedOrganization === 'personal'
                            ? 'This project will only be visible to you'
                            : 'All organization members will be able to access and collaborate on this project'}
                        </Caption>
                      </div>
                    )}

                    <Input
                      label={
                        <>
                          Repository Name{' '}
                          <span className="text-destructive">*</span>
                        </>
                      }
                      type="text"
                      value={newRepoName}
                      onChange={e => setNewRepoName(e.target.value)}
                      placeholder="my-awesome-project"
                      required
                    />

                    <div>
                      <label className="block text-sm font-semibold mb-3 text-text">
                        Description
                      </label>
                      <textarea
                        value={newRepoDescription}
                        onChange={e => setNewRepoDescription(e.target.value)}
                        placeholder="A brief description of your project..."
                        rows={3}
                        className="w-full rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 resize-none"
                        style={{
                          padding: spacing.form.input,
                          border: '1px solid rgb(var(--color-input))',
                          backgroundColor: 'rgb(var(--color-background))',
                          color: 'rgb(var(--color-text))',
                          borderRadius: 'var(--radius-lg)',
                          transition: 'var(--transition-base)',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = 'rgb(var(--color-border-focus))';
                          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(var(--color-primary), 0.2)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgb(var(--color-input))';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="isPrivate"
                        checked={isPrivate}
                        onChange={e => setIsPrivate(e.target.checked)}
                        className="w-4 h-4 rounded focus:ring-2 focus:ring-offset-2"
                        style={{
                          accentColor: 'rgb(var(--color-primary))',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      />
                      <label
                        htmlFor="isPrivate"
                        className="text-sm text-text"
                      >
                        Make this repository private
                      </label>
                    </div>
                  </div>

                  <div 
                    className="flex gap-3 pt-4 flex-shrink-0"
                    style={{
                      borderTop: '1px solid rgb(var(--color-border))',
                    }}
                  >
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onClose}
                      className="flex-1"
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="default"
                      className="flex-1"
                      disabled={loading || !newRepoName.trim()}
                      title={
                        !newRepoName.trim()
                          ? 'Please enter a repository name'
                          : 'Create new repository and project'
                      }
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          Creating Repository...
                        </span>
                      ) : (
                        'Create Repository & Project'
                      )}
                    </Button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </DialogContent>

      <ConfirmModal
        isOpen={upgradeConfirm.isOpen}
        onClose={() =>
          setUpgradeConfirm({ isOpen: false, message: '', upgradeUrl: '' })
        }
        onConfirm={() => {
          window.location.href = upgradeConfirm.upgradeUrl;
        }}
        title='Upgrade Required'
        message={`${upgradeConfirm.message}\n\nWould you like to upgrade your plan?`}
        confirmText='Upgrade'
        cancelText='Cancel'
        confirmVariant='primary'
        loading={false}
      />
    </Dialog>
  );
}
